import { noop } from "@core/foundation/function";
import type { Promisable } from "@core/foundation/promise";
import type { Stage } from "@core/foundation/stage";
import { getQualifiedDomainName } from "@core/foundation/stage";
import type { ReadonlyRecord } from "@core/foundation/types";
import { hasValue, isNullish } from "@core/foundation/types";
import { z } from "@core/zod";

import type {
  AnyApiCallerFn,
  AnyApiCallers,
  ApiCaller,
  ApiCallers,
  ApiClientContract,
  GetToken,
} from "./ApiClientContract.ts";
import type { AnyApi } from "./api.ts";
import { GeneralErrorBodySchema } from "./error-response.ts";
import { HttpStatusError } from "./HttpStatusError.ts";
import type { HttpMethod } from "./http-method.ts";
import { StatusCodes } from "./status-codes.ts";

export interface FetchRequest {
  readonly body?: string;
  readonly headers?: ReadonlyRecord<string, string>;
  readonly method: HttpMethod;
  readonly url: string;
}

export interface FetchResponse {
  readonly json: () => Promisable<unknown>;
  readonly status: number;
}

export abstract class BaseApiClient<Api extends AnyApi>
  implements ApiClientContract<Api>
{
  public readonly __typeTag = "ApiClientContract";

  public readonly api: Api;

  public readonly getToken: GetToken<Api>;

  private readonly onUnauthorized: () => void;

  private readonly qualifiedDomain: string;

  public constructor({
    api,
    getToken,
    onUnauthorized = noop,
    stage,
  }: {
    readonly api: Api;
    readonly getToken: GetToken<Api>;
    readonly onUnauthorized?: () => void;
    readonly stage: Stage;
  }) {
    this.api = api;
    this.getToken = getToken;
    this.onUnauthorized = onUnauthorized;
    this.qualifiedDomain = getQualifiedDomainName(this.api.domain, stage);
  }

  public getApi(): ApiCallers<Api> {
    const result: AnyApiCallers = Object.fromEntries(
      Object.keys(this.api.endpoints).map(key => [key, this.getCaller(key)]),
    );

    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return result as ApiCallers<Api>;
  }

  public getCaller<E extends keyof Api["endpoints"] & string>(
    endpointKey: E,
  ): ApiCaller<Api["endpoints"][E]> {
    const endpoint = this.api.endpoints[endpointKey];

    if (isNullish(endpoint)) {
      throw new Error("Endpoint not found", { cause: { endpointKey } });
    }

    const getCanonicalRequest = (...args: readonly unknown[]): unknown => {
      const hasBody = hasValue(endpoint.body);

      const hasQueryParams = hasValue(endpoint.queryParams);

      if (hasBody && hasQueryParams) {
        return args[0];
      }

      if (hasBody) {
        return { body: args[0] };
      }

      if (hasQueryParams) {
        return { queryParams: args[0] };
      }

      return undefined;
    };

    const getBody = (request: unknown): unknown => {
      if (!endpoint.body) {
        return undefined;
      }

      const BodySchema = z.object(
        { body: endpoint.body },
        { body: "The request's body" },
      );

      const { body } = BodySchema.parse(request);

      return body;
    };

    const getQueryParams = (request: unknown): unknown => {
      if (!endpoint.queryParams) {
        return undefined;
      }

      const QueryParamsSchema = z.object(
        { queryParams: endpoint.queryParams },
        { queryParams: "The request's query parameters" },
      );

      const { queryParams } = QueryParamsSchema.parse(request);

      return queryParams;
    };

    const callerFn: AnyApiCallerFn = async (
      ...args: readonly unknown[]
    ): Promise<unknown> => {
      const request = getCanonicalRequest(...args);

      const bodyValue = getBody(request);
      const body = hasValue(bodyValue) ? JSON.stringify(bodyValue) : undefined;

      // @mutable
      const headers: Record<string, string> = {};

      if (this.api.authType !== "public") {
        const token = await this.getToken();

        if (isNullish(token)) {
          throw new Error("The token callback answered no token", {
            cause: { domain: this.api.domain, endpointKey },
          });
        }

        headers["Authorization"] = `Bearer ${token}`;
      }

      if (hasValue(body)) {
        headers["Content-Type"] = "application/json";
      }

      const queryParamsValue = getQueryParams(request);
      const QueryParamsSchema = z.record(z.coerce.string().optional());
      const queryParams = hasValue(queryParamsValue)
        ? QueryParamsSchema.parse(queryParamsValue)
        : {};

      const url = new URL(this.getEndpointUrl(endpointKey));

      for (const [key, value] of Object.entries(queryParams)) {
        if (hasValue(value)) {
          url.searchParams.set(key, value);
        }
      }

      const response = await this.fetch({
        body,
        headers,
        method: endpoint.method,
        url: url.toString(),
      });

      if (response.status === StatusCodes.UNAUTHORIZED) {
        this.onUnauthorized();
      }

      if (response.status >= StatusCodes.BAD_REQUEST) {
        const userPresentableMessage =
          await this.readUserPresentableMessage(response);

        throw HttpStatusError.fromStatus({
          method: endpoint.method,
          statusCode: response.status,
          url: url.toString(),
          userPresentableMessage,
        });
      }

      const json =
        response.status === StatusCodes.NO_CONTENT ||
        response.status === StatusCodes.NOT_MODIFIED ||
        response.status === StatusCodes.RESET_CONTENT
          ? undefined
          : await response.json();

      return endpoint.response.parse(json);
    };

    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return callerFn as ApiCaller<Api["endpoints"][E]>;
  }

  public getEndpointUrl(endpointKey: keyof Api["endpoints"] & string): string {
    const segments = this.api.path
      ? [this.api.path, endpointKey]
      : [endpointKey];

    return `https://${this.qualifiedDomain}/${segments.join("/")}`;
  }

  protected abstract fetch(request: FetchRequest): Promisable<FetchResponse>;

  private async readUserPresentableMessage(
    response: FetchResponse,
  ): Promise<string | undefined> {
    try {
      const json = await response.json();
      const result = GeneralErrorBodySchema.safeParse(json);

      return result.success ? result.data.userPresentableMessage : undefined;
    } catch {
      return undefined;
    }
  }
}
